/* What the card's amount-due pill says about the visit's coverage.

   Verified means someone checked this visit's coverage:
   - 9928 (and any practice with the coverage picker): the front desk picked
     Covered / Coinsurance / Deductible in Payments. The pick is stored in
     `deductiblemet` (see COVERAGE_OPTIONS in modals/payments.jsx); the keys
     below are those stored codes, not colours. Athena's eligibility alone
     does not count here, but an Eligible / Ineligible result is named in
     the Unverified pill's hover text.
   - 7825 (OSA, practiceFeatures().osaPayments): OSA has no coverage picker,
     so Athena's eligibility check on the visit (`eligibilitystatus` Eligible
     / Ineligible, from the v1 dashboard) is its verification.

   Anything else (blank, "0" after Clear, an unknown code, no eligibility
   check, FHIR's "active") is Unverified: a neutral pill, never the
   Coinsurance look. Staff verify the day before or at check-in, so nearly
   every future visit is Unverified, and its $0 is not a real amount: copay is
   an INT NOT NULL column that stores "unknown" as 0. */
export const COVERAGE_STATUS = {
  "58D68D": { label: "Covered", color: "#2e9e62" },
  F9E79F: { label: "Coinsurance", color: "#e17954" },
  F3082C: { label: "Deductible", color: "#6d54a2" },
};

const ELIGIBILITY_STATUS = {
  ELIGIBLE: { label: "Eligible", color: "#2e9e62" },
  INELIGIBLE: { label: "Ineligible", color: "#c0392b" },
};

const VERIFIED_TEXT = "#fff";

export const UNVERIFIED = Object.freeze({
  label: "Unverified",
  color: "#eef1f5",
  textColor: "#4b5563",
  verified: false,
  source: null,
});

const codeOf = (code) => String(code ?? "").replace("#", "").trim().toUpperCase();

// The front desk picked a coverage status for this visit.
export const isCoverageVerified = (code) => Boolean(COVERAGE_STATUS[codeOf(code)]);

// The status a stored `deductiblemet` code stands for; Unverified when none
// was picked.
export const coverageStatus = (code) => {
  const picked = COVERAGE_STATUS[codeOf(code)];
  return picked
    ? { ...picked, textColor: VERIFIED_TEXT, verified: true, source: "frontdesk" }
    : UNVERIFIED;
};

// The co-insurance % the front desk saved for a Coinsurance (F9E79F) visit:
// v1 updatepaymentupdate keeps it in the visit's `percentage` JSON as
// {"Percentage": n, "Amount": ...} (the column may arrive as text, and the
// number as a string). Null when the visit is not Coinsurance or no % > 0 is
// stored (a Deductible visit stores 100 there by rule, not a typed value).
export const savedCoinsurancePercent = (row) => {
  if (codeOf(row?.deductiblemet) !== "F9E79F") return null;
  let p = row?.percentage;
  if (typeof p === "string") {
    try {
      p = JSON.parse(p);
    } catch (e) {
      return null;
    }
  }
  const n = parseFloat(p?.Percentage);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// The visit's status: the front desk's pick first, then (usesEligibility, a
// practice without the picker) Athena's eligibility check.
export const visitCoverage = (row, { usesEligibility = false } = {}) => {
  const picked = coverageStatus(row?.deductiblemet);
  if (picked.verified) {
    const percent = savedCoinsurancePercent(row);
    return percent == null ? picked : { ...picked, percent };
  }
  const elig = ELIGIBILITY_STATUS[String(row?.eligibilitystatus ?? "").trim().toUpperCase()];
  if (!usesEligibility) {
    // Still Unverified until the front desk picks, but Athena's result (e.g.
    // Ineligible) is named in the hover text rather than dropped.
    return elig
      ? { ...UNVERIFIED, athena: elig.label, checked: row?.eligibilitylastchecked || null }
      : UNVERIFIED;
  }
  if (!elig) return { ...UNVERIFIED, source: "eligibility" };
  return {
    ...elig,
    textColor: VERIFIED_TEXT,
    verified: true,
    source: "eligibility",
    checked: row?.eligibilitylastchecked || null,
  };
};

const checkedOn = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return m ? ` (checked ${m[2]}/${m[3]}/${m[1]})` : "";
};

// Hover text for the pill.
export const coverageTitle = (status) => {
  if (status.verified) {
    return status.source === "eligibility"
      ? `Amount Due. Athena eligibility: ${status.label}${checkedOn(status.checked)}.`
      : status.percent != null
        ? `Amount Due. Coinsurance ${status.percent}%.`
        : "Amount Due";
  }
  if (status.source === "eligibility") {
    return "Unverified: Athena has no eligibility check for this visit yet. It fills in when eligibility is checked, before or at check-in.";
  }
  if (status.athena) {
    return `Unverified: the front desk has not picked Covered, Coinsurance or Deductible in Payments for this visit yet. Athena eligibility: ${status.athena}${checkedOn(status.checked)}.`;
  }
  return "Unverified: coverage has not been checked for this visit yet. It fills in when the front desk verifies eligibility in Payments, before or at check-in.";
};

// The pill's amount. Only an unverified $0 becomes a dash (it is not known
// yet); any other amount still shows as before, including a credit (a
// negative account balance, e.g. "$-25").
export const pillAmount = (status, amount) => {
  const n = Number(amount);
  return status.verified || (Number.isFinite(n) && n !== 0) ? `$${amount}` : "—";
};
